// Reglas puras de «Actualizar»: la revisión manual de una entidad contra Zentral.
import { ReconciliationRunStatusEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationRunStatusEnum';
import { ApiError } from '@/api/errors';

export const RETAIL_RECONCILE_PERMISSION = 'retail.integration.reconcile';

/** Cada cuánto se consulta el avance de la corrida mientras sigue abierta. */
export const RECONCILIATION_POLL_MS = 3000;

/** Reintentos de la consulta del avance ante fallos pasajeros. */
export const POLL_MAX_RETRIES = 2;

const ALREADY_RUNNING = 'RECONCILIATION_ALREADY_RUNNING';
const NOT_INTEGRATED = 'RETAILER_NOT_INTEGRATED';

export type StartFailure =
    | { kind: 'alreadyRunning'; runId: string | null }
    | { kind: 'notIntegrated' }
    | { kind: 'forbidden' }
    | { kind: 'failed' };

export function isRunFinished(status: ReconciliationRunStatusEnum): boolean {
    return status !== ReconciliationRunStatusEnum.RUNNING;
}

/** Intervalo para `refetchInterval`: se detiene al terminar la corrida o si la consulta falló. */
export function pollIntervalOf(
    status: ReconciliationRunStatusEnum | undefined,
    failed: boolean,
): number | false {
    if (failed) return false;
    if (status !== undefined && isRunFinished(status)) return false;
    return RECONCILIATION_POLL_MS;
}

/** `retry` de la consulta del avance: solo fallos de red y 5xx, hasta `POLL_MAX_RETRIES`. */
export function shouldRetryPoll(failureCount: number, error: unknown): boolean {
    if (failureCount >= POLL_MAX_RETRIES) return false;
    return !(error instanceof ApiError) || error.httpStatus >= 500;
}

/** El runId que el 409 trae en `data`, si viene. */
function runIdOf(data: unknown): string | null {
    if (typeof data !== 'object' || data === null || !('runId' in data)) return null;
    const { runId } = data;
    return typeof runId === 'string' && runId ? runId : null;
}

/** Qué hacer cuando el POST de arranque se rechaza. */
export function startFailureOf(error: unknown): StartFailure {
    if (!(error instanceof ApiError)) return { kind: 'failed' };
    if (error.httpStatus === 403) return { kind: 'forbidden' };
    if (error.code === NOT_INTEGRATED) return { kind: 'notIntegrated' };
    if (error.code === ALREADY_RUNNING)
        return { kind: 'alreadyRunning', runId: runIdOf(error.data) };
    return { kind: 'failed' };
}

/** Intentos de SQS por página antes de abortar; el GET lo manda en `maxAttempts`. */
export const DEFAULT_MAX_ATTEMPTS = 5;

const ZENTRAL_DOWN_CODES: ReadonlySet<string> = new Set([
    'RETAIL_INTEGRATION_CONNECTOR_UNAVAILABLE',
]);

/** Por qué se abortó una corrida, agrupado por lo que el usuario tiene que hacer. */
export type AbortKind = 'zentralDown' | 'maxAttempts' | 'leaseTaken' | 'unknown' | 'other';

/** El `code` es de Zentral o del connector caído. */
export function isZentralDown(code: string | null | undefined): boolean {
    return code !== null && code !== undefined && ZENTRAL_DOWN_CODES.has(code);
}

/** Traduce `abortReason`; `unknown` cuando el backend no lo manda. */
export function abortKindOf(reason: string | null | undefined): AbortKind {
    if (!reason) return 'unknown';
    if (isZentralDown(reason)) return 'zentralDown';
    if (reason === 'MAX_ATTEMPTS') return 'maxAttempts';
    if (reason === 'LEASE_TAKEN') return 'leaseTaken';
    return 'other';
}
